import type { ActionCreator as ActionCreatorRedux, UnknownAction, Store } from '@reduxjs/toolkit';

/**
 * Набор произвольных action creator'ов
 * Используется в условных типах и дженериках как правая часть extends
 *
 * @internal
 */
export type AnyActionCreatorsMap = Record<string, AnyActionCreator>;

/**
 * Произвольный action creator
 * Используется в условных типах и дженериках как правая часть extends
 *
 * @internal
 */
export type AnyActionCreator = ActionCreatorRedux<UnknownAction>;

/**
 * Обернутый action creator, который можно вызывать без `store.dispatch()`
 * Получается как результат вызова `bindActionCreator()`
 *
 * @typeParam ActionCreator - Action creator, который будет передан в `bindActionCreator()`
 * @internal
 */
export type BoundActionCreator<ActionCreator extends AnyActionCreator> = (
    /** Если в экшене нет payload, то формируем функцию без аргументов */
    ...args: Parameters<ActionCreator> extends [noArgument?: void] ? [] : Parameters<ActionCreator>
) => void;

/**
 * Привязывает action creator к стору
 *
 * @param getStore – Функция, возвращающая стор, к которому будет привязан экшен
 * @param actionCreator – Action creator, который будет привязан к стору
 * @returns Обернутый action creator, который можно вызывать без `store.dispatch()`
 * @internal
 */
export function bindActionCreator<ActionCreator extends AnyActionCreator>(
    getStore: () => Store,
    actionCreator: ActionCreator
): BoundActionCreator<ActionCreator> {
    /** Если в экшене нет payload, то формируем функцию без аргументов */
    return function (...args: Parameters<ActionCreator> extends [noArgument?: void] ? [] : Parameters<ActionCreator>) {
        getStore().dispatch(actionCreator(...args));
    };
}
